import type { ContentProcessorState } from '../contentProcessor.types';

import { createErrorDirective } from '../../utils/directives';
import { parseErrorMessage } from '../../utils/errorsHelper';

/**
 * When processing a historical (non-streaming) message that produced no
 * content — and did not terminate in error/cancellation — surface an error
 * directive so the UI doesn't render a blank assistant message.
 *
 * For streaming messages this is a no-op: validation runs once streaming
 * finishes and the message is fetched fresh from the server.
 */
export function validateResult(
  result: ContentProcessorState,
): ContentProcessorState {
  if (result.options.isStreaming) {
    return result;
  }
  if (result.nodes.length !== 0) {
    return result;
  }

  const { events } = result.message;
  // Empty events == not-yet-started turn, not a zero-content completion.
  // The WS harness pre-creates an assistant row (content='', event_stream=nil)
  // before inference runs (harness_ws.go CreateMessage), so any consumer that
  // fetches messages mid-turn would otherwise see a spurious error directive.
  // A genuine finished turn always emits at least a start+done pair.
  if (events.length === 0) {
    return result;
  }
  const lastEvent = events[events.length - 1];
  const terminatedAbnormally =
    lastEvent.event === 'error' || lastEvent.event === 'canceled';
  if (terminatedAbnormally) {
    return result;
  }
  // A `paused` terminal frame means the LLM handed off to a client tool
  // (AskUserQuestion). The turn legitimately has no rendered content — the
  // form takes over the surface — so don't paint the 'no content' error.
  if (lastEvent.event === 'paused') {
    return result;
  }

  const errorData = { message: 'No content was generated for this message.' };
  const errorMessage = parseErrorMessage(
    errorData.message,
    'An error occurred',
    result.options.isDebugMode,
  );
  const markdown = createErrorDirective(errorMessage);

  return {
    ...result,
    nodes: [{ type: 'text', content: markdown }],
    chunks: {
      ...result.chunks,
      error: [...result.chunks.error, errorData],
    },
  };
}
